import TableBody from '@mui/material/TableBody';
import Table from '@mui/material/Table';
import LoadingRows from '../../../../components/Table/LoadingRows';
import Row from '../../../../components/Table/Row';
import NoDataCell from '../../../../components/NoDataCell';
import { pagesText } from '../../../../constants/pagesText';
import useResetSearchParams from '../../../../hooks/useResetSearchParams';
import TableContainer from '../../../../components/Table/TableContainer';
import { Box, Paper, Stack, TableCell, TableHead, TableRow } from '@mui/material';
import useTypographyMediaQuery from 'src/hooks/useTypographyMediaQuery';
import TableHeadTypography from 'src/components/TableHeadTypography';
import { defaultSearchParams, reversedSortMap, ROW_SOURCES, sortingMethods } from 'src/constants/table';
import TableSearch from 'src/components/elements/TableSearch';
import useQueryParams from 'src/hooks/useQueryParams';
import React, { memo, useCallback, useMemo, useState } from 'react';
import useDebounceHandler from 'src/hooks/useDebounceHandler';
import useTableSort from 'src/hooks/useTableSort';
import { checkHasFilters } from 'src/utils/table.util';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import EditDataModal from './EditDataModal';
import GenericTableCell from 'src/components/GenericTableCell';
import { isDateString } from 'src/utils/date.util';
import { formatDate } from 'src/utils/formatTime';
import { WDAC_PACKAGED_APP_QUERY_KEY, WDAC_TABS_DATA_COUNTS_KEY } from 'src/constants/query';
import useSubscribeStorage from 'src/hooks/useSubscribeStorage';
import { DEFAULT_ENVIRONMENT_ID } from 'src/constants/storage';
import StatusButton from 'src/components/StatusButton';
import WdacPackagedAppByDeviceIdSidebar from './WdacPackagedAppByDeviceIdSidebar';
import Tooltip from '@mui/material/Tooltip';
import Image from '../../../../components/Image';
import { trustedStatus, unknownStatus } from './constants';
import { defaultPackagedAppsFilters } from '../../../../components/Filters/constants';
import FilterCount from '../../../../components/Filters/FilterCount';
import Checkbox from '@mui/material/Checkbox';
import { BulkStatusSelect } from '../../../wdac-results/tabs/PackagedApps/BulkStatusSelect';
import WDACPackagedAppService from '../../../../services/wdac-packaged-app.service';

const PackagedAppsByDeviceIdTable = ({
                                       isLoading,
                                       results,
                                       lastElementRef,
                                       isFetching,
                                       topHeading,
                                       secondHeadings,
                                       columnsOrder,
                                       totalCount,
                                       query,
                                     }) => {
  const queryClient = useQueryClient();
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);
  const [isOpenFilters, setIsOpenFilters] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const [modal, setModal] = useState({
    open: false,
    id: null,
    currentStatus: '',
    currentNote: '',
    currentDate: null,
  });

  const { enqueueSnackbar } = useSnackbar();

  const { mutate: updatePackagedAppStatus, isLoading: statusLoading } = useMutation(
    WDACPackagedAppService.updateGlobalPackagedAppStatus,
    {
      onSuccess: async () => {
        enqueueSnackbar('Status updated', { variant: 'success', autoHideDuration: 3000 });
        await queryClient.setQueryData([WDAC_TABS_DATA_COUNTS_KEY], prevData => {
          if (!prevData) return prevData;
          const newData = {
            ...prevData,
            packedAppsCount: !hasFilters
              ? Math.max((prevData.packedAppsCount || 0) - 1, 0)
              : prevData.packedAppsCount,
          };

          return newData;
        });
        setModal(prev => ({ ...prev, open: false }));
        await queryClient.invalidateQueries([WDAC_PACKAGED_APP_QUERY_KEY, query, customerEnvId]);
      },
      onError: () => {
        enqueueSnackbar(pagesText.somethingWentWrong, { variant: 'error', autoHideDuration: 3000 });
      },
    },
  );


  const renderCustomCell = useCallback((column, row) => {
    if (column.name === 'status') {
      const buttonLabel = row.status === unknownStatus ? 'No Status' : row.status.replace(/([a-z])([A-Z])/g, '$1 $2');
      const status = row.status;
      const signatureKind = row.signatureKind;
      const showInfo = status === trustedStatus && signatureKind === 'System';
      const isSelected = selectedIds.includes(row.id);
      const label = {
        inputProps: { 'aria-label': 'Checkbox demo' },
        checked: isSelected,
        onChange: (e) => {
          console.log(e.target.checked);
          if (e.target.checked) {
            setSelectedIds((prev) => [...prev, row.id]);
          } else {
            setSelectedIds((prev) => prev.filter((id) => id !== row.id));
          }
        },
      };
      return (
        <TableCell
          key="status"
          onClick={(e) => e.stopPropagation()}
          sx={{ p: 0, minWidth: '130px' }}
        >
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            sx={{
              position: 'relative',
              '&:hover .hover-checkbox': {
                opacity: 1,
              },
            }}
          >
            <Box
              className="hover-checkbox"
              sx={{
                opacity: isSelected ? 1 : 0.3,
                transition: 'opacity 0.2s',
                mr: 1,
                minWidth: '42px',
              }}
            >
              {!showInfo && <Checkbox {...label} />}
            </Box>
            {row.notes && (
              <Tooltip
                placement="top"
                title={row.notes}
                componentsProps={{
                  tooltip: {
                    sx: { bgcolor: '#3F3F3F' },
                  },
                }}
              >
                <Box sx={{
                  mr: 1,
                  display: 'flex',
                  alignItems: 'center',
                  width: 24,
                  height: 24,
                }}>
                  <Image src="/static/icons/notes-notepad.svg" alt="Notes Icon" width={24} height={24} />
                </Box>
              </Tooltip>
            )}

            <StatusButton
              status={row.status}
              size="small"
              disabled={showInfo}
              sx={{
                fontSize: '12px',
                padding: '4px',
                ml: !row.notes ? '28px' : 0,
              }}
              onClick={() => setModal({
                open: true,
                id: row.id,
                currentStatus: row.status || '',
                currentNote: row.notes || '',
                currentDate: row.statusUpdatedOn || null,
              })}
            >
              {buttonLabel}
            </StatusButton>
          </Stack>
        </TableCell>
      );
    }
    return (
      <GenericTableCell
        key={column?.name || column}
        isNumber={column?.isNumber}
        value={
          column?.shouldHideValue
            ? ''
            : isDateString(row[column?.name || column])
              ? formatDate(row[column?.name || column])
              : row[column?.name || column]
        }
        styles={column?.styles || {}}
        {...column?.props}
      />
    );
  }, [selectedIds, setSelectedIds]);

  useResetSearchParams();

  const {
    queryObject: { page, rowsPerPage, filters = {}, searchValue: initialSearchValue },
    changeFieldInURL,
    onApplySearchParams,
  } = useQueryParams();

  const [searchValue, setSearchValue] = useState(initialSearchValue);

  const { mediumScreen, largeScreen, smallScreen } = useTypographyMediaQuery();

  const { onSort, onResetSortField, getListItemSortMethod, getCellSortMethod } = useTableSort({
    page,
    rowsPerPage,
    filters,
    onApplySearchParams,
    searchValue,
    defaultFilters: defaultPackagedAppsFilters,
  });

  const debounceQueryHandler = useDebounceHandler((key, value) => {
    changeFieldInURL([{ key, value }]);
  }, 500);

  const onCloseFilters = useCallback(() => {
    setIsOpenFilters(false);
  }, [setIsOpenFilters]);

  const onOpenFilters = useCallback(() => {
    setIsOpenFilters(true);
  }, [setIsOpenFilters]);

  const onChangeSearchValue = useCallback(
    event => {
      setSearchValue(event.target.value);
      debounceQueryHandler('search', event.target.value.trim());
    },
    [debounceQueryHandler, setSearchValue],
  );

  const onCellSort = useCallback(
    ({ field, sortingCellField, sortMethod, sortFieldName }) => {
      const reversedSortOrder =
        sortMethod === sortingMethods.initial ? sortingMethods.asc : reversedSortMap[sortMethod];
      onSort(field, reversedSortOrder, sortingCellField, sortFieldName);
    },
    [onSort],
  );

  const onResetSearch = useCallback(() => {
    setSearchValue('');
    changeFieldInURL([defaultSearchParams]);
  }, [setSearchValue, changeFieldInURL]);

  const onApplyFilters = useCallback(
    filters => {
      onApplySearchParams({ filters, page: 1, limit: rowsPerPage, searchValue });
    },
    [onApplySearchParams, rowsPerPage, searchValue],
  );

  const hasFilters = useMemo(() => checkHasFilters(filters), [filters]);

  const filteredCount = useMemo(() => {
    return !isLoading && (hasFilters || searchValue) ? totalCount : null;
  }, [totalCount, hasFilters, isLoading, searchValue]);

  return (
    <Box sx={{ position: 'relative' }}>
      {selectedIds?.length > 0 && <BulkStatusSelect top='-38px' selectedIds={selectedIds} setSelectedIds={setSelectedIds} query={query} filteredCount={filteredCount} />}
       <FilterCount filteredCount={filteredCount} onOpenFilters={onOpenFilters}/>
     <Paper
        elevation={0}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'unset',
          mt: 4,
        }}>
        <TableSearch
          onResetSearch={onResetSearch}
          onChangeSearchValue={onChangeSearchValue}
          searchValue={searchValue}
        />
        <TableContainer
          styles={{ maxHeight: `calc(100vh - 354px)` }}
          square
          component={Paper}
          elevation={0}>
          <Table stickyHeader aria-label="collapsible table">
            <TableHead sx={{ backgroundColor: '#3f3f3f', borderRadius: 0 }}>
              <TableRow>
                {topHeading.map((el, index) => {
                  return (
                    <TableCell
                      key={`${el.title}-${index}`}
                      mediumScreen={mediumScreen}
                      largeScreen={largeScreen}
                      colSpan={el.colSpan}
                      rowSpan={el.rowSpan}
                      sx={{
                        top: '0',
                        p: 1,
                        borderRight: el.borderRight ? '2px solid #2e3236' : 'unset',
                      }}>
                      <Stack gap={1} direction="row" alignItems="center" justifyContent={'center'}>
                        <TableHeadTypography
                          mediumScreen={mediumScreen}
                          largeScreen={largeScreen}
                          smallScreen={smallScreen}>
                          {el.title}
                        </TableHeadTypography>
                      </Stack>
                    </TableCell>
                  );
                })}
              </TableRow>
              <TableRow>
                {secondHeadings.map(({ Component, ...headingProps }, index) => {
                  const sortMethod = !getListItemSortMethod
                    ? sortingMethods.initial
                    : getListItemSortMethod(headingProps?.name);

                  return (
                    <TableCell
                      key={`${headingProps.title}-${index}`}
                      mediumScreen={mediumScreen}
                      largeScreen={largeScreen}
                      colSpan={1}
                      sx={{
                        p: '8px 16px',
                        top: '31px',
                        borderRight: headingProps.borderRight ? '2px solid #2e3236' : 'unset',
                        zIndex: 3,
                        ...(headingProps.width ? { width: headingProps.width } : {}),
                      }}>
                      <Stack gap={1} direction="row" alignItems="center">
                        {!!Component ? (
                          <Component
                            title={headingProps.title}
                            name={headingProps.name}
                            onDoubleClick={onResetSortField}
                            onCellSort={onCellSort}
                            getCellSortMethod={getCellSortMethod}
                            filterFieldName={headingProps.filterFieldName}
                            sortingCellField={headingProps.sortingCellField}
                            sortFieldName={headingProps.sortFieldName}
                            field={headingProps.field}
                          />
                        ) : (
                          <TableHeadTypography
                            onDoubleClick={() => onResetSortField(headingProps.name)}
                            mediumScreen={mediumScreen}
                            largeScreen={largeScreen}
                            smallScreen={smallScreen}
                            hasSorting={sortMethod !== sortingMethods.initial}>
                            {headingProps.title}
                          </TableHeadTypography>
                        )}
                      </Stack>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody sx={{ backgroundColor: '#4B4B4B' }}>
              {isLoading ? (
                <LoadingRows columnCount={7} rowCount={5} />
              ) : (
                <>
                  {results?.map((row, index) => {
                    const isUniqueRow =
                      row.eventSystemTime &&
                      row.statusUpdatedOn &&
                      new Date(row.eventSystemTime) > new Date(row.statusUpdatedOn);

                    return (
                      <Row
                        key={`${row.id}-${index}`}
                        withTwoColors={true}
                        isClickable={true}
                        ref={results?.length - 1 === index ? lastElementRef : null}
                        row={row}
                        columnData={columnsOrder}
                        hasActions={false}
                        source={ROW_SOURCES.packagedApps}
                        isUniqueRow={isUniqueRow}
                        renderCustomCell={renderCustomCell}
                      />
                    );
                  })}
                  {!isLoading && isFetching && <LoadingRows columnCount={7} rowCount={3} />}
                  {!isLoading && !results?.length && (
                    <NoDataCell colSpan={columnsOrder.length + 1}>
                      {pagesText.noDataFound}
                    </NoDataCell>
                  )}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <WdacPackagedAppByDeviceIdSidebar
          open={isOpenFilters}
          onClose={onCloseFilters}
          onApplyFilters={onApplyFilters}
          initialQueryFilters={filters}
        />
      </Paper>
      <EditDataModal
        open={modal.open}
        onClose={() => setModal(prev => ({ ...prev, open: false }))}
        currentStatus={modal.currentStatus}
        currentNote={modal.currentNote}
        currentDate={modal.currentDate}
        onSave={(newStatus, note) => {
          if (!modal.id) return;
          updatePackagedAppStatus({
            customerEnvId: customerEnvId,
            globalPackagedAppId: modal.id,
            status: newStatus,
            notes: note,
          });
        }}
        loading={statusLoading}
      />

    </Box>
  );
};

export default memo(PackagedAppsByDeviceIdTable);
